import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { hand } from '@cribbage/engine';
import { CARD_FRONTS, type CardFront } from '~/store/cardFronts.js';
import { PlayingCard } from '~/table/PlayingCard.js';
import { Button } from './Button.js';
import { FieldLabel } from './FieldLabel.js';

const SWATCH = hand('5D', 'JC');

/** The same overlapping number and court preview in every settings surface. */
export function CardFrontPicker({
  value,
  fourColor,
  onChange,
  tag,
}: {
  readonly value: CardFront;
  readonly fourColor: boolean;
  readonly onChange: (value: CardFront) => void;
  /** A mark pinned to the preview's corner, such as In use or Members. Positions itself. */
  readonly tag?: ReactNode;
}): React.JSX.Element {
  const { t } = useTranslation();
  const labelId = useId();
  const currentIndex = Math.max(0, CARD_FRONTS.indexOf(value));
  const move = (offset: number): void => {
    const next = CARD_FRONTS[(currentIndex + offset + CARD_FRONTS.length) % CARD_FRONTS.length];
    if (next !== undefined) onChange(next);
  };
  return (
    <div className="flex flex-col gap-3 py-2 text-ink" role="group" aria-labelledby={labelId}>
      <FieldLabel id={labelId} hint={t('settings.cardFrontHint')}>
        {t('settings.cardFront')}
      </FieldLabel>
      <div className="mx-auto flex w-full max-w-80 items-center justify-between">
        <Button
          variant="ghost"
          size="sm"
          aria-label={t('settings.cardFrontPrevious')}
          onClick={() => {
            move(-1);
          }}
        >
          <CaretLeftIcon className="size-4 shrink-0" aria-hidden="true" focusable="false" />
        </Button>
        <div className="card-front-preview relative">
          {SWATCH.map((card) => (
            <div key={card} className="card-front-preview-card">
              <PlayingCard
                card={card}
                cardFront={value}
                fourColour={fourColor}
                size="picker-preview"
              />
            </div>
          ))}
          {tag}
        </div>
        <Button
          variant="ghost"
          size="sm"
          aria-label={t('settings.cardFrontNext')}
          onClick={() => {
            move(1);
          }}
        >
          <CaretRightIcon className="size-4 shrink-0" aria-hidden="true" focusable="false" />
        </Button>
      </div>
      <div className="text-center" aria-live="polite">
        <p className="truncate text-sm leading-snug text-ink">
          {t(`settings.cardFronts.${value}`)}
        </p>
        <p className="truncate text-sm text-muted">
          {t('settings.cardBackPosition', {
            current: currentIndex + 1,
            total: CARD_FRONTS.length,
          })}
        </p>
      </div>
    </div>
  );
}
